import { useEffect, useState } from "react";
import { Save, WalletCards } from "lucide-react";
import { toast } from "sonner";
import api, { getError } from "@/api";

const roleLabels = {
  manager: "Manager",
  livreur: "Livreur",
  coordinateur_general: "Coordinateur général",
  coordinateur_zone: "Coordinateur de zone",
};

const defaultCommission = { low_price_unit: 0, high_price_unit: 0 };

export default function CommissionManagementPage() {
  const [users, setUsers] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [savingId, setSavingId] = useState("");

  const load = async () => {
    try {
      const response = await api.get("/auth/users");
      const eligibleUsers = response.data.filter((user) => roleLabels[user.role]);
      setUsers(eligibleUsers);
      setDrafts(Object.fromEntries(eligibleUsers.map((user) => [
        user.user_id,
        { ...defaultCommission, ...user.commission },
      ])));
    } catch (error) {
      toast.error(getError(error));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const updateDraft = (userId, field, value) => {
    setDrafts((current) => ({
      ...current,
      [userId]: { ...current[userId], [field]: value },
    }));
  };

  const save = async (userId) => {
    setSavingId(userId);
    try {
      await api.patch(`/auth/users/${userId}/commission`, {
        low_price_unit: Number(drafts[userId].low_price_unit || 0),
        high_price_unit: Number(drafts[userId].high_price_unit || 0),
      });
      toast.success("Commission enregistrée.");
      await load();
    } catch (error) {
      toast.error(getError(error));
    } finally {
      setSavingId("");
    }
  };

  return (
    <div className="page-enter commission-page" data-testid="commission-management-page">
      <div className="page-heading">
        <div>
          <p className="eyebrow">PARAMÈTRES UTILISATEURS</p>
          <h1 data-testid="commission-management-title">Commissions de livraison</h1>
          <p>Montant fixe par unité, selon le prix de vente du produit.</p>
        </div>
        <WalletCards aria-hidden="true" size={28} />
      </div>
      <section className="data-panel" data-testid="commission-users-panel">
        <div className="commission-price-legend" data-testid="commission-threshold-legend">
          <span>Palier bas : prix de vente ≤ 200 000 GNF</span>
          <span>Palier haut : prix de vente &gt; 200 000 GNF</span>
        </div>
        <div className="commission-list">
          {users.map((user) => (
            <article data-testid={`commission-user-${user.user_id}`} key={user.user_id}>
              <div>
                <strong>{user.name}</strong>
                <span>{roleLabels[user.role]}</span>
              </div>
              <label>
                <span>Palier bas (GNF / unité)</span>
                <input
                  data-testid={`commission-low-${user.user_id}`}
                  min="0"
                  onChange={(event) => updateDraft(user.user_id, "low_price_unit", event.target.value)}
                  step="1"
                  type="number"
                  value={drafts[user.user_id]?.low_price_unit ?? 0}
                />
              </label>
              <label>
                <span>Palier haut (GNF / unité)</span>
                <input
                  data-testid={`commission-high-${user.user_id}`}
                  min="0"
                  onChange={(event) => updateDraft(user.user_id, "high_price_unit", event.target.value)}
                  step="1"
                  type="number"
                  value={drafts[user.user_id]?.high_price_unit ?? 0}
                />
              </label>
              <button
                className="secondary-action"
                data-testid={`save-commission-${user.user_id}`}
                disabled={savingId === user.user_id}
                onClick={() => save(user.user_id)}
                type="button"
              >
                <Save size={16} />
                {savingId === user.user_id ? "Enregistrement…" : "Enregistrer"}
              </button>
            </article>
          ))}
        </div>
        {!users.length && <p className="empty-inline">Aucun rôle à commission configuré.</p>}
      </section>
    </div>
  );
}